import { useEffect, useRef, useState } from "react";
import { Search, X } from "./icons";
import { useLanguage } from "../context/LanguageContext";

const EXAMPLES_RU = [
  "печь конвекционная",
  "фритюрница",
  "холодильный шкаф",
  "тарелки Tognana",
  "закаточная машина",
  "миксер планетарный",
  "плита индукционная",
  "посудомоечная машина",
];

const EXAMPLES_KK = [
  "конвекциялық пеш",
  "фритюрница",
  "тоңазытқыш шкаф",
  "Tognana тәрелкелері",
  "жабу машинасы",
  "планетарлық миксер",
  "индукциялық плита",
  "ыдыс жуу машинасы",
];

const TYPE_MS = 70;
const DELETE_MS = 35;
const HOLD_MS = 1400;
const GAP_MS = 400;

// Applied identically to the ghost-text overlay and the real <input> —
// font-size alone matching wasn't enough for them to render at the same
// visual size (see the ghost-text JSX comment below), so every property
// that affects glyph metrics is pinned explicitly here rather than left to
// each element's own inheritance.
const SHARED_TEXT_CLASSES = "font-sans text-sm font-normal leading-none tracking-normal sm:text-base";

export default function AnimatedSearchInput({
  value,
  onChange,
  className = "",
  inputClassName = "",
  iconSize = 22,
  onClear,
  showClear,
  ghostSuggestion = "",
  onAcceptGhost,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  inputClassName?: string;
  iconSize?: number;
  onClear?: () => void;
  showClear?: boolean;
  /** Full suggested completion (typed text + remainder), e.g. typing
   * "тарелка глуб" with this set to "тарелка глубокая купе" renders
   * "купе" as inline gray ghost text after the cursor. Ignored unless it
   * actually starts with the current value (case-insensitive) — the
   * caller may be a keystroke behind an async suggestions response. */
  ghostSuggestion?: string;
  /** Called with the full ghost suggestion when the visitor accepts it
   * (Tab or → at the end of the input). Omit to disable that shortcut. */
  onAcceptGhost?: (fullValue: string) => void;
}) {
  const { t, language } = useLanguage();
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const examples = language === "kz" ? EXAMPLES_KK : EXAMPLES_RU;
  const animate = !isFocused && value === "";

  useEffect(() => {
    if (!animate) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }

    let exampleIndex = 0;
    let charIndex = 0;
    let phase: "typing" | "holding" | "deleting" | "gap" = "typing";

    const tick = () => {
      const current = examples[exampleIndex % examples.length];
      if (phase === "typing") {
        charIndex += 1;
        setAnimatedPlaceholder(current.slice(0, charIndex));
        if (charIndex >= current.length) {
          phase = "holding";
          timeoutRef.current = setTimeout(tick, HOLD_MS);
        } else {
          timeoutRef.current = setTimeout(tick, TYPE_MS);
        }
        return;
      }
      if (phase === "holding") {
        phase = "deleting";
        timeoutRef.current = setTimeout(tick, DELETE_MS);
        return;
      }
      if (phase === "deleting") {
        charIndex -= 1;
        setAnimatedPlaceholder(current.slice(0, charIndex));
        if (charIndex <= 0) {
          phase = "gap";
          exampleIndex += 1;
          timeoutRef.current = setTimeout(tick, GAP_MS);
        } else {
          timeoutRef.current = setTimeout(tick, DELETE_MS);
        }
        return;
      }
      // gap
      phase = "typing";
      timeoutRef.current = setTimeout(tick, TYPE_MS);
    };

    timeoutRef.current = setTimeout(tick, TYPE_MS);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate, language]);

  // Only offered while actively typing (not on the animated empty-state
  // placeholder above), and only when the suggestion is actually a valid
  // completion of what's typed — the caller's suggestion can lag a
  // keystroke behind on a slow connection, so this re-checks rather than
  // trusting the prop blindly.
  const showGhost =
    isFocused &&
    value.length > 0 &&
    ghostSuggestion.length > value.length &&
    ghostSuggestion.toLowerCase().startsWith(value.toLowerCase());
  const ghostRemainder = showGhost ? ghostSuggestion.slice(value.length) : "";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showGhost || !onAcceptGhost) return;
    const input = e.currentTarget;
    const atEnd = input.selectionStart === value.length && input.selectionEnd === value.length;
    if (atEnd && (e.key === "Tab" || e.key === "ArrowRight")) {
      e.preventDefault();
      onAcceptGhost(ghostSuggestion);
    }
  };

  return (
    <div
      className={`group flex h-14 min-w-0 items-center gap-3 border border-steel-200 bg-white px-5 shadow-sm transition-colors duration-200 ease-engineered focus-within:border-accent focus-within:shadow-md ${className}`}
    >
      <Search size={iconSize} strokeWidth={1.5} className="shrink-0 text-steel-400 transition-colors duration-200 ease-engineered group-focus-within:text-accent" />
      <div className="relative min-w-0 flex-1">
        {animate && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 flex items-center whitespace-nowrap text-sm text-steel-400 sm:text-base"
          >
            {animatedPlaceholder}
            <span className="ml-px inline-block h-[1.1em] w-px animate-pulse bg-steel-400 align-middle" />
          </span>
        )}
        {/* Ghost overlay and the real <input> below must share the exact
            same typography classes (SHARED_TEXT_CLASSES) — an earlier
            version relied on both independently landing on matching
            text-sm/text-base, but they rendered at visibly different
            sizes (a native <input>'s font metrics don't always inherit
            identically to a plain <div>'s, even with matching Tailwind
            classes, unless line-height/tracking/padding are pinned down
            explicitly too). right-0 + overflow-hidden bounds the ghost
            row to the input's own width; the remainder span additionally
            gets min-w-0 (flex items default to min-width:auto, which
            silently blocks text-ellipsis from ever triggering) so a long
            suggestion truncates instead of overflowing into the ФИЛЬТРЫ
            button next to it. Always mounted (never conditionally
            rendered) and faded via opacity so a suggestion arriving or
            disappearing crossfades instead of popping in/out instantly. */}
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-y-0 left-0 right-0 z-0 flex items-center overflow-hidden whitespace-nowrap transition-opacity duration-150 ease-engineered ${
            showGhost ? "opacity-100" : "opacity-0"
          } ${SHARED_TEXT_CLASSES}`}
        >
          <span className="invisible shrink-0">{value}</span>
          <span className="min-w-0 flex-1 overflow-hidden text-ellipsis text-steel-400">
            {ghostRemainder}
          </span>
        </div>
        <input
          type="text"
          value={value}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={animate ? "" : t("catalogSearchPlaceholder")}
          className={`relative z-10 m-0 w-full min-w-0 border-0 bg-transparent p-0 text-ink outline-none placeholder:text-steel-400 ${SHARED_TEXT_CLASSES} ${inputClassName}`}
        />
      </div>
      {(showClear ?? value !== "") && (
        <button
          type="button"
          aria-label={t("searchClear")}
          onClick={() => (onClear ? onClear() : onChange(""))}
          className="shrink-0 text-steel-400 transition-colors duration-200 ease-engineered hover:text-accent"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
}
